import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle, 
  Target, 
  Zap,
  BarChart3,
  Lightbulb,
  Settings,
  Play,
  Pause,
  RefreshCw,
  Eye,
  ArrowUp,
  ArrowDown,
  Minus
} from 'lucide-react';
import Icon from '../../../components/AppIcon';


const PerformanceOptimizer = ({ funnelData }) => {
  const [activeTest, setActiveTest] = useState(null);
  const [optimizationMode, setOptimizationMode] = useState('automatic');

  // Mock optimization data
  const optimizationInsights = [
    {
      id: 1,
      type: 'critical',
      title: 'Low Landing Page Conversion',
      description: 'Your landing page conversion rate is 15% below industry average',
      impact: 'high',
      recommendation: 'Test headline variations and simplify your form',
      potentialLift: '+23%',
      priority: 1,
      status: 'actionable'
    },
    {
      id: 2,
      type: 'warning',
      title: 'Email Open Rates Declining',
      description: 'Email open rates have decreased by 8% over the last 30 days',
      impact: 'medium',
      recommendation: 'A/B test subject lines and sender names',
      potentialLift: '+12%',
      priority: 2,
      status: 'testing'
    },
    {
      id: 3,
      type: 'success',
      title: 'High Email Click-through Rate',
      description: 'Your email CTR is 34% above industry benchmark',
      impact: 'positive',
      recommendation: 'Scale this approach to other campaigns',
      potentialLift: 'maintaining',
      priority: 3,
      status: 'optimized'
    },
    {
      id: 4,
      type: 'opportunity',
      title: 'Untapped Mobile Traffic',
      description: '45% of traffic is mobile but conversion is 60% lower',
      impact: 'high',
      recommendation: 'Implement mobile-specific funnel optimization',
      potentialLift: '+31%',
      priority: 1,
      status: 'actionable'
    }
  ];

  const abTests = [
    {
      id: 1,
      name: 'Landing Page Headlines',
      status: 'running',
      startDate: '2025-01-05',
      variants: [
        { name: 'Control', visitors: 2340, conversions: 351, rate: 15.0 },
        { name: 'Variant A', visitors: 2287, conversions: 389, rate: 17.0 },
        { name: 'Variant B', visitors: 2198, conversions: 374, rate: 17.0 }
      ],
      winner: null,
      confidence: 85,
      timeLeft: '3 days'
    },
    {
      id: 2,
      name: 'Email Subject Lines',
      status: 'completed',
      startDate: '2024-12-20',
      variants: [
        { name: 'Control', visitors: 5000, conversions: 1200, rate: 24.0 },
        { name: 'Variant A', visitors: 5000, conversions: 1450, rate: 29.0 }
      ],
      winner: 'Variant A',
      confidence: 99,
      timeLeft: 'Complete'
    }
  ];

  const performanceMetrics = [
    {
      metric: 'Conversion Rate',
      current: 14.95,
      benchmark: 18.2,
      trend: 'down',
      change: -2.1,
      status: 'needs_improvement'
    },
    {
      metric: 'Email Open Rate',
      current: 22.8,
      benchmark: 21.3,
      trend: 'up',
      change: 1.5,
      status: 'good'
    },
    {
      metric: 'Click-through Rate',
      current: 8.4,
      benchmark: 6.2,
      trend: 'up',
      change: 2.2,
      status: 'excellent'
    },
    {
      metric: 'Cost per Conversion',
      current: 24.50,
      benchmark: 28.00,
      trend: 'down',
      change: -12.5,
      status: 'excellent'
    }
  ];

  const getInsightIcon = (type) => {
    switch (type) {
      case 'critical': return AlertTriangle;
      case 'warning': return AlertTriangle;
      case 'success': return CheckCircle;
      case 'opportunity': return Lightbulb;
      default: return Target;
    }
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'critical': return 'border-red-200 bg-red-50 text-red-700';
      case 'warning': return 'border-orange-200 bg-orange-50 text-orange-700';
      case 'success': return 'border-green-200 bg-green-50 text-green-700';
      case 'opportunity': return 'border-blue-200 bg-blue-50 text-blue-700';
      default: return 'border-gray-200 bg-gray-50 text-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTrendIcon = (trend, change) => {
    if (trend === 'up') return <ArrowUp className="w-4 h-4 text-green-500" />;
    if (trend === 'down') return <ArrowDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
  };

  const getMetricStatus = (status) => {
    switch (status) {
      case 'excellent': return 'bg-green-100 text-green-800';
      case 'good': return 'bg-blue-100 text-blue-800';
      case 'needs_improvement': return 'bg-orange-100 text-orange-800';
      case 'poor': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Performance Optimizer</h2>
          <p className="text-gray-600">AI-powered insights and optimization recommendations</p>
        </div>
        
        <div className="flex items-center space-x-4 mt-4 lg:mt-0">
          <select
            value={optimizationMode}
            onChange={(e) => setOptimizationMode(e?.target?.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="automatic">Automatic Optimization</option>
            <option value="manual">Manual Control</option>
            <option value="hybrid">Hybrid Mode</option>
          </select>
          
          <button className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-colors">
            <RefreshCw className="w-4 h-4" />
            <span>Refresh Insights</span>
          </button>
        </div>
      </div>
      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {performanceMetrics?.map((metric, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg p-6 shadow-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-600">{metric?.metric}</h3>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMetricStatus(metric?.status)}`}>
                {metric?.status?.replace('_', ' ')}
              </span>
            </div>
            
            <div className="flex items-center justify-between mb-2">
              <div className="text-2xl font-bold text-gray-900">
                {metric?.metric?.includes('Rate') ? `${metric?.current}%` : 
                 metric?.metric?.includes('Cost') ? `$${metric?.current}` : metric?.current}
              </div>
              <div className="flex items-center space-x-1">
                {getTrendIcon(metric?.trend)}
                <span className={`text-sm font-medium ${
                  metric?.change > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {Math.abs(metric?.change)}%
                </span>
              </div>
            </div>
            
            <div className="text-xs text-gray-500">
              Benchmark: {metric?.metric?.includes('Rate') ? `${metric?.benchmark}%` : 
                          metric?.metric?.includes('Cost') ? `$${metric?.benchmark}` : metric?.benchmark}
            </div>
          </motion.div>
        ))}
      </div>
      {/* Optimization Insights */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center space-x-2">
          <Lightbulb className="w-5 h-5 text-yellow-500" />
          <span>AI Optimization Insights</span>
        </h3>
        
        <div className="space-y-4">
          {optimizationInsights?.map((insight, index) => {
            const Icon = getInsightIcon(insight?.type);
            return (
              <motion.div
                key={insight?.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`p-4 rounded-lg border-2 ${getInsightColor(insight?.type)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <Icon className="w-5 h-5 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="font-semibold">{insight?.title}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          insight?.impact === 'high' ? 'bg-red-100 text-red-800' :
                          insight?.impact === 'medium'? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {insight?.impact} impact
                        </span>
                      </div>
                      <p className="text-sm mb-3">{insight?.description}</p>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">
                          💡 {insight?.recommendation}
                        </p>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-semibold text-green-600">
                            {insight?.potentialLift}
                          </span>
                          <span className="text-xs text-gray-500">potential lift</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2 ml-4">
                    {insight?.status === 'actionable' && (
                      <button className="px-3 py-1 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors">
                        Implement
                      </button>
                    )}
                    {insight?.status === 'testing' && (
                      <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors">
                        View Test
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
      {/* A/B Tests */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <Target className="w-5 h-5 text-purple-600" />
            <span>A/B Tests</span>
          </h3>
          <button className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-colors">
            <Play className="w-4 h-4" />
            <span>New Test</span>
          </button>
        </div>
        
        <div className="space-y-6">
          {abTests?.map((test) => (
            <div key={test?.id} className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{test?.name}</h4>
                  <p className="text-sm text-gray-600">
                    Started: {new Date(test.startDate)?.toLocaleDateString()} • 
                    {test?.status === 'running' ? ` ${test?.timeLeft} remaining` : ' Completed'}
                  </p>
                </div>
                
                <div className="flex items-center space-x-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(test?.status)}`}>
                    {test?.status}
                  </span>
                  
                  {test?.confidence && (
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-900">
                        {test?.confidence}% confidence
                      </div>
                      {test?.winner && (
                        <div className="text-xs text-green-600">
                          Winner: {test?.winner}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex space-x-2">
                    <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                      <Eye className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                      {test?.status === 'running' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                    <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                      <Settings className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {test?.variants?.map((variant, index) => (
                  <div 
                    key={index} 
                    className={`p-4 rounded-lg border-2 ${
                      variant?.name === test?.winner 
                        ? 'border-green-300 bg-green-50' :'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium text-gray-900">{variant?.name}</h5>
                      {variant?.name === test?.winner && (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Visitors:</span>
                        <span className="font-medium">{variant?.visitors?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Conversions:</span>
                        <span className="font-medium">{variant?.conversions?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Rate:</span>
                        <span className="font-semibold text-purple-600">{variant?.rate}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Quick Actions */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Optimization Actions</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="flex items-center justify-center space-x-2 p-4 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors">
            <Zap className="w-5 h-5" />
            <span>Auto-optimize Headlines</span>
          </button>
          
          <button className="flex items-center justify-center space-x-2 p-4 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors">
            <BarChart3 className="w-5 h-5" />
            <span>Analyze Traffic Sources</span>
          </button>
          
          <button className="flex items-center justify-center space-x-2 p-4 border border-green-300 text-green-700 rounded-lg hover:bg-green-50 transition-colors">
            <TrendingUp className="w-5 h-5" />
            <span>Conversion Audit</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PerformanceOptimizer;